import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-1fd69da5"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "card" }
const _hoisted_2 = { class: "card-header" }
const _hoisted_3 = { class: "card-title" }
const _hoisted_4 = { class: "card-toolbar" }
const _hoisted_5 = { class: "d-flex align-items-center gap-2 flex-wrap" }
const _hoisted_6 = { class: "d-flex align-items-center gap-1" }
const _hoisted_7 = { class: "text-muted" }
const _hoisted_8 = { class: "card-body" }
const _hoisted_9 = {
  key: 0,
  class: "status-info-row mb-4"
}
const _hoisted_10 = { class: "status-label" }
const _hoisted_11 = { class: "status-count" }
const _hoisted_12 = { class: "table-responsive" }
const _hoisted_13 = { class: "table align-middle table-row-dashed fs-6 gy-2 table-hover" }
const _hoisted_14 = { class: "text-start text-muted fw-bold fs-7 text-uppercase gs-0" }
const _hoisted_15 = { key: 0 }
const _hoisted_16 = { key: 1 }
const _hoisted_17 = { key: 2 }
const _hoisted_18 = { key: 3 }

import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import MsgPrompt from "@/core/plugins/MsgPrompt";
import RebateService from "../services/RebateService";

type PlatformTradeDetail = {
  totalTradeCount?: number;
  totalVolume?: number;
  totalProfit?: number;
};

type TradeSyncItem = {
  hour: number;
  tradeDetails?: Record<string, PlatformTradeDetail>;
  rebateDetails?: Record<string, number>;
  totalTradeCount?: number;
  totalVolume?: number;
  totalProfit?: number;
  totalRebateCount?: number;
};

type TableRow = {
  hour: number;
  totalTradeCount: number;
  totalVolume: number;
  totalProfit: number;
  totalRebateCount: number;
  mt5TradeCount: number;
  mt5Volume: number;
  mt5Profit: number;
  mt5RebateCount: number;
  mt4TradeCount: number;
  mt4Volume: number;
  mt4Profit: number;
  mt4RebateCount: number;
};

type TradeSyncStatusInfoItem = {
  status: number;
  count: number;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'RebateStatistics',
  setup(__props) {

const { t } = useI18n();

const selectedDate = ref("");
const isLoading = ref(false);
const hasSearched = ref(false);
const rows = ref<TableRow[]>([]);
const statusInfo = ref<TradeSyncStatusInfoItem[]>([]);
const autoRefreshEnabled = ref(false);
const refreshSeconds = ref(20);
let autoRefreshTimer: ReturnType<typeof setInterval> | null = null;

const toNumber = (value: unknown): number => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const formatNumber = (value: number): string => {
  return toNumber(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const normalizeData = (list: TradeSyncItem[]): TableRow[] => {
  const byHour = new Map<number, TradeSyncItem>();
  list.forEach((item) => byHour.set(toNumber(item.hour), item));

  const normalized: TableRow[] = [];

  for (let hour = 0; hour < 24; hour++) {
    const item = byHour.get(hour);
    const tradeDetails = item?.tradeDetails || {};
    const rebateDetails = item?.rebateDetails || {};

    const mt5Trade = tradeDetails["30"] || {};
    const mt4Trade = tradeDetails["20"] || tradeDetails["10"] || {};

    normalized.push({
      hour,
      totalTradeCount: toNumber(item?.totalTradeCount),
      totalVolume: toNumber(item?.totalVolume),
      totalProfit: toNumber(item?.totalProfit),
      totalRebateCount: toNumber(item?.totalRebateCount),
      mt5TradeCount: toNumber(mt5Trade.totalTradeCount),
      mt5Volume: toNumber(mt5Trade.totalVolume),
      mt5Profit: toNumber(mt5Trade.totalProfit),
      mt5RebateCount: toNumber(rebateDetails["30"]),
      mt4TradeCount: toNumber(mt4Trade.totalTradeCount),
      mt4Volume: toNumber(mt4Trade.totalVolume),
      mt4Profit: toNumber(mt4Trade.totalProfit),
      mt4RebateCount: toNumber(rebateDetails["20"] ?? rebateDetails["10"]),
    });
  }

  return normalized;
};

const fetchStatusInfo = async () => {
  try {
    const statusResponse = await RebateService.getTradeSyncStatusInfo();
    const statusList: TradeSyncStatusInfoItem[] = Array.isArray(statusResponse)
      ? statusResponse
      : statusResponse?.data || [];

    statusInfo.value = statusList.map((item) => ({
      status: toNumber(item.status),
      count: toNumber(item.count),
    }));
  } catch {
    statusInfo.value = [];
  }
};

const search = async () => {
  if (!selectedDate.value) {
    return;
  }

  isLoading.value = true;
  hasSearched.value = true;

  try {
    const response = await RebateService.getTradeSyncStatisticsV2(
      selectedDate.value
    );
    const list: TradeSyncItem[] = Array.isArray(response)
      ? response
      : response?.data || [];
    rows.value = normalizeData(list);
  } catch (error: any) {
    rows.value = [];
    MsgPrompt.error(
      error?.response?.data?.message || error?.message || "Error"
    );
  } finally {
    isLoading.value = false;
  }
};

const reset = () => {
  selectedDate.value = "";
  hasSearched.value = false;
  rows.value = [];
  statusInfo.value = [];
};

const statusInfoDisplay = computed(() => {
  return statusInfo.value.map((item) => ({
    status: item.status,
    count: item.count,
    label: t(`type.tradeRebateStatus.${item.status}`),
  }));
});

const stopAutoRefresh = () => {
  if (autoRefreshTimer) {
    clearInterval(autoRefreshTimer);
    autoRefreshTimer = null;
  }
};

const startAutoRefresh = () => {
  stopAutoRefresh();

  if (!autoRefreshEnabled.value) {
    return;
  }

  const seconds = Math.max(1, Number(refreshSeconds.value) || 20);
  autoRefreshTimer = setInterval(() => {
    fetchStatusInfo();
    if (!isLoading.value && hasSearched.value && selectedDate.value) {
      search();
    }
  }, seconds * 1000);
};

watch([autoRefreshEnabled, refreshSeconds, selectedDate, hasSearched], () => {
  startAutoRefresh();
});

onUnmounted(() => {
  stopAutoRefresh();
});

onMounted(() => {
  fetchStatusInfo();
});

const mt5Label = computed(() => t("type.platform.30"));
const mt4Label = computed(() => t("type.platform.10"));

const totalTradeCountLabel = computed(
  () => `${t("title.total")}${t("fields.tradeCount")}`
);
const totalVolumeLabel = computed(() => t("fields.totalVolumes"));
const totalProfitLabel = computed(() => t("fields.totalProfits"));
const totalRebateCountLabel = computed(
  () => `${t("title.total")}${t("fields.rebateCount")}`
);

const mt5TradeCountLabel = computed(
  () => `${mt5Label.value}${t("title.total")}${t("fields.tradeCount")}`
);
const mt5VolumeLabel = computed(
  () => `${mt5Label.value}${t("fields.totalVolumes")}`
);
const mt5ProfitLabel = computed(
  () => `${mt5Label.value}${t("fields.totalProfits")}`
);
const mt5RebateCountLabel = computed(
  () => `${mt5Label.value}${t("title.total")}${t("fields.rebateCount")}`
);

const mt4TradeCountLabel = computed(
  () => `${mt4Label.value}${t("title.total")}${t("fields.tradeCount")}`
);
const mt4VolumeLabel = computed(
  () => `${mt4Label.value}${t("fields.totalVolumes")}`
);
const mt4ProfitLabel = computed(
  () => `${mt4Label.value}${t("fields.totalProfits")}`
);
const mt4RebateCountLabel = computed(
  () => `${mt4Label.value}${t("title.total")}${t("fields.rebateCount")}`
);

return (_ctx: any,_cache: any) => {
  const _component_el_date_picker = _resolveComponent("el-date-picker")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_checkbox = _resolveComponent("el-checkbox")!
  const _component_el_input_number = _resolveComponent("el-input-number")!
  const _component_LoadingRing = _resolveComponent("LoadingRing")!
  const _component_NoDataBox = _resolveComponent("NoDataBox")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, _toDisplayString(_ctx.$t("title.rebateStatistics")), 1),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _createVNode(_component_el_date_picker, {
            modelValue: selectedDate.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedDate).value = $event)),
            type: "date",
            placeholder: _ctx.$t('fields.date'),
            format: "YYYY-MM-DD",
            "value-format": "YYYY-MM-DD",
            class: "w-175px"
          }, null, 8, ["modelValue", "placeholder"]),
          _createVNode(_component_el_button, {
            type: "primary",
            disabled: !selectedDate.value || isLoading.value,
            onClick: search
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t("action.search")), 1)
            ]),
            _: 1
          }, 8, ["disabled"]),
          _createVNode(_component_el_button, {
            disabled: isLoading.value,
            onClick: reset
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t("action.clear")), 1)
            ]),
            _: 1
          }, 8, ["disabled"]),
          _createVNode(_component_el_checkbox, {
            modelValue: autoRefreshEnabled.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((autoRefreshEnabled).value = $event))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t("fields.autoRefresh")), 1)
            ]),
            _: 1
          }, 8, ["modelValue"]),
          _createElementVNode("div", _hoisted_6, [
            _createVNode(_component_el_input_number, {
              modelValue: refreshSeconds.value,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((refreshSeconds).value = $event)),
              min: 1,
              max: 3600,
              step: 1,
              controls: true,
              size: "default"
            }, null, 8, ["modelValue"]),
            _createElementVNode("span", _hoisted_7, _toDisplayString(_ctx.$t("fields.seconds")), 1)
          ])
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_8, [
      (statusInfoDisplay.value.length > 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(statusInfoDisplay.value, (item) => {
              return (_openBlock(), _createElementBlock("div", {
                key: item.status,
                class: "status-info-item"
              }, [
                _createElementVNode("span", _hoisted_10, _toDisplayString(item.label), 1),
                _createElementVNode("span", _hoisted_11, _toDisplayString(item.count), 1)
              ]))
            }), 128))
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_12, [
        _createElementVNode("table", _hoisted_13, [
          _createElementVNode("thead", null, [
            _createElementVNode("tr", _hoisted_14, [
              _createElementVNode("th", null, _toDisplayString(_ctx.$t("fields.hour")), 1),
              _createElementVNode("th", null, _toDisplayString(totalTradeCountLabel.value), 1),
              _createElementVNode("th", null, _toDisplayString(totalVolumeLabel.value), 1),
              _createElementVNode("th", null, _toDisplayString(totalProfitLabel.value), 1),
              _createElementVNode("th", null, _toDisplayString(totalRebateCountLabel.value), 1),
              _createElementVNode("th", null, _toDisplayString(mt5TradeCountLabel.value), 1),
              _createElementVNode("th", null, _toDisplayString(mt5VolumeLabel.value), 1),
              _createElementVNode("th", null, _toDisplayString(mt5ProfitLabel.value), 1),
              _createElementVNode("th", null, _toDisplayString(mt5RebateCountLabel.value), 1),
              _createElementVNode("th", null, _toDisplayString(mt4TradeCountLabel.value), 1),
              _createElementVNode("th", null, _toDisplayString(mt4VolumeLabel.value), 1),
              _createElementVNode("th", null, _toDisplayString(mt4ProfitLabel.value), 1),
              _createElementVNode("th", null, _toDisplayString(mt4RebateCountLabel.value), 1)
            ])
          ]),
          (isLoading.value)
            ? (_openBlock(), _createElementBlock("tbody", _hoisted_15, [
                _createVNode(_component_LoadingRing)
              ]))
            : (!hasSearched.value)
              ? (_openBlock(), _createElementBlock("tbody", _hoisted_16))
              : (rows.value.length === 0)
                ? (_openBlock(), _createElementBlock("tbody", _hoisted_17, [
                    _createVNode(_component_NoDataBox)
                  ]))
                : (_openBlock(), _createElementBlock("tbody", _hoisted_18, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rows.value, (row) => {
                      return (_openBlock(), _createElementBlock("tr", {
                        key: row.hour
                      }, [
                        _createElementVNode("td", null, _toDisplayString(row.hour), 1),
                        _createElementVNode("td", null, _toDisplayString(row.totalTradeCount), 1),
                        _createElementVNode("td", null, _toDisplayString(formatNumber(row.totalVolume)), 1),
                        _createElementVNode("td", null, _toDisplayString(formatNumber(row.totalProfit)), 1),
                        _createElementVNode("td", null, _toDisplayString(row.totalRebateCount), 1),
                        _createElementVNode("td", null, _toDisplayString(row.mt5TradeCount), 1),
                        _createElementVNode("td", null, _toDisplayString(formatNumber(row.mt5Volume)), 1),
                        _createElementVNode("td", null, _toDisplayString(formatNumber(row.mt5Profit)), 1),
                        _createElementVNode("td", null, _toDisplayString(row.mt5RebateCount), 1),
                        _createElementVNode("td", null, _toDisplayString(row.mt4TradeCount), 1),
                        _createElementVNode("td", null, _toDisplayString(formatNumber(row.mt4Volume)), 1),
                        _createElementVNode("td", null, _toDisplayString(formatNumber(row.mt4Profit)), 1),
                        _createElementVNode("td", null, _toDisplayString(row.mt4RebateCount), 1)
                      ]))
                    }), 128))
                  ]))
        ])
      ])
    ])
  ]))
}
}

})